import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { MAIL_APP_HEADER_TITLE } from "@/auth/constants";
import { MailHeaderBrandIcon } from "@/features/shell/MailHeaderBrandIcon";
import { cn } from "@/lib/cn";
import { MobileButton } from "@/mobile/components/ui/MobileButton";

const CLOSE_DRAG_THRESHOLD = 72;

type Props = {
  open: boolean;
  title?: string;
  /** Header marque (logo + titre app) à la place du titre texte. */
  brand?: boolean;
  onClose: () => void;
  children: ReactNode;
};

/** Drawer gauche — slide-in, fermeture par glisser vers la gauche ou backdrop. */
export function MobileDrawer({ open, title, brand = false, onClose, children }: Props) {
  const panelRef = useRef<HTMLDivElement>(null);
  const touchStartXRef = useRef(0);
  const dragXRef = useRef(0);
  const draggingRef = useRef(false);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const [visible, setVisible] = useState(open);
  const [dragX, setDragX] = useState(0);
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    if (open) {
      setVisible(true);
      setDragX(0);
      return;
    }
    const timer = window.setTimeout(() => setVisible(false), 280);
    return () => window.clearTimeout(timer);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const setDrag = useCallback((value: number) => {
    const clamped = Math.max(-320, Math.min(0, value));
    dragXRef.current = clamped;
    setDragX(clamped);
  }, []);

  useEffect(() => {
    const panel = panelRef.current;
    if (!panel || !open) return;

    const onTouchStart = (e: TouchEvent) => {
      const touch = e.touches[0];
      if (!touch) return;
      touchStartXRef.current = touch.clientX;
      draggingRef.current = true;
      setDragging(true);
    };

    const onTouchMove = (e: TouchEvent) => {
      if (!draggingRef.current) return;
      const touch = e.touches[0];
      if (!touch) return;

      const dx = touch.clientX - touchStartXRef.current;
      if (dx < 0) {
        e.preventDefault();
        setDrag(dx);
      }
    };

    const onTouchEnd = () => {
      setDragging(false);
      draggingRef.current = false;
      if (dragXRef.current <= -CLOSE_DRAG_THRESHOLD) {
        onCloseRef.current();
        setDrag(0);
        return;
      }
      setDrag(0);
    };

    panel.addEventListener("touchstart", onTouchStart, { passive: true });
    panel.addEventListener("touchmove", onTouchMove, { passive: false });
    panel.addEventListener("touchend", onTouchEnd, { passive: true });
    panel.addEventListener("touchcancel", onTouchEnd, { passive: true });

    return () => {
      panel.removeEventListener("touchstart", onTouchStart);
      panel.removeEventListener("touchmove", onTouchMove);
      panel.removeEventListener("touchend", onTouchEnd);
      panel.removeEventListener("touchcancel", onTouchEnd);
    };
  }, [open, setDrag]);

  if (!visible) return null;

  const panelTransform =
    dragX !== 0 ? `translateX(${dragX}px)` : open ? "translateX(0)" : "translateX(-100%)";

  return createPortal(
    <div className="mobile-drawer-root fixed inset-0 z-50" role="dialog" aria-modal="true">
      <button
        type="button"
        className={cn(
          "absolute inset-0 bg-black/45 transition-opacity duration-250",
          open ? "opacity-100" : "opacity-0"
        )}
        aria-label="Fermer le menu"
        onClick={onClose}
      />
      <aside
        ref={panelRef}
        className="mobile-drawer-panel absolute inset-y-0 left-0 flex w-[min(20rem,88vw)] flex-col shadow-xl"
        style={{
          transform: panelTransform,
          transition: dragging ? undefined : "transform 0.28s cubic-bezier(0.32, 0.72, 0, 1)",
        }}
      >
        <header className="flex shrink-0 items-center justify-between gap-2 border-b border-[var(--mail-border-soft)] px-4 py-3">
          {brand ? (
            <div className="flex min-w-0 items-center gap-2">
              <span className="mcv-app-header__icon-ring shrink-0" aria-hidden="true">
                <MailHeaderBrandIcon />
              </span>
              <h2 className="truncate text-sm font-semibold text-[var(--mail-text)]">
                {MAIL_APP_HEADER_TITLE}
              </h2>
            </div>
          ) : (
            <h2 className="text-sm font-semibold text-[var(--mail-text)]">{title ?? "Menu"}</h2>
          )}
          <MobileButton variant="icon" onClick={onClose} aria-label="Fermer">
            <X className="h-5 w-5" aria-hidden />
          </MobileButton>
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain">{children}</div>
      </aside>
    </div>,
    document.body
  );
}
